import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Check, CreditCard, Calendar, Clock, PartyPopper, RefreshCw,
  AlertTriangle, Shield, Users, FileText, BarChart3, MessageSquare,
  Settings, Package, Star, Zap, Download, Mail, Lock, Landmark, Building2
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { sortInvoicesForDisplay } from "../lib/invoices";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MODULE_ICONS = {
  employees: Users,
  habilitations: Shield,
  conges: Calendar,
  payroll: FileText,
  documents: FileText,
  entretiens: MessageSquare,
  expenses: FileText,
  rse: BarChart3,
  ai_chat: MessageSquare,
  calendar: Calendar,
  admin: Settings,
  dashboard: BarChart3
};

// Allowed roles for this page
const ALLOWED_ROLES = ['admin', 'rh', 'comptabilite', 'super_admin'];

const MODULE_LABELS = {
  employees: 'Gestion des employés',
  habilitations: 'Habilitations',
  conges: 'Gestion des congés',
  payroll: 'Préparation de la paie',
  documents: 'Documents',
  entretiens: 'Entretiens',
  expenses: 'Notes de frais',
  rse: 'RSE',
  ai_chat: 'Assistant IA',
  calendar: 'Calendrier',
  admin: 'Administration',
  dashboard: 'Tableau de bord'
};

const Subscription = ({ embedded = false }) => {
  const { user, refreshUser, allowedModules, allModulesAccess } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [packages, setPackages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [invoicePending, setInvoicePending] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [downloading, setDownloading] = useState(null);
  const [activeTab, setActiveTab] = useState('subscription');
  const [isUpgradeMode, setIsUpgradeMode] = useState(false);
  const [showPaymentMethodDialog, setShowPaymentMethodDialog] = useState(false);
  const [pendingPackage, setPendingPackage] = useState(null);
  const [pendingPlanType, setPendingPlanType] = useState(null);
  const [acceptConditions, setAcceptConditions] = useState(false);
  const [showSepaProcessing, setShowSepaProcessing] = useState(false);
  const [processingPaymentMethod, setProcessingPaymentMethod] = useState('sepa_debit');
  const [contract, setContract] = useState(null);
  const [downloadingContract, setDownloadingContract] = useState(false);

  // Check access (skip when embedded in Admin page)
  useEffect(() => {
    if (!embedded && user && !ALLOWED_ROLES.includes(user.role)) {
      toast.error('Accès non autorisé');
      navigate('/');
    }
  }, [user, navigate, embedded]);

  useEffect(() => {
    // Fetch data when user is loaded and has correct role
    if (user && (embedded || ALLOWED_ROLES.includes(user.role))) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const action = urlParams.get('action');

    if (sessionId) {
      checkPaymentStatus(sessionId);
    }

    // Handle upgrade action from module access denial
    if (action === 'upgrade') {
      setActiveTab('subscription');
      setIsUpgradeMode(true);
      toast.info('Sélectionnez un forfait supérieur pour accéder à plus de modules');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      // Fetch subscription, packages, invoices and contract in parallel
      const [subResponse, packagesResponse, invoicesResponse, contractResponse] = await Promise.all([
        fetch(`${API_URL}/subscriptions/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/packages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/invoices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/subscription-contract/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (subResponse.ok) {
        const subData = await subResponse.json();
        setSubscription(subData);
      } else {
        // No subscription found is OK
        setSubscription(null);
      }

      if (packagesResponse.ok) {
        const packagesData = await packagesResponse.json();
        setPackages(packagesData);
      }

      if (invoicesResponse.ok) {
        const invoicesData = await invoicesResponse.json();
        // Tri par période facturée puis date de facture (plus récente en premier)
        const sorted = sortInvoicesForDisplay(invoicesData);
        setInvoices(sorted);
      }

      if (contractResponse.ok) {
        const contractData = await contractResponse.json();
        setContract(contractData);
      } else {
        setContract(null);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // La facture est créée par le webhook Stripe (invoice.paid) : après un paiement réussi,
  // on interroge GET /invoices quelques fois pour l'afficher dès qu'elle est disponible.
  const waitForNewInvoice = async () => {
    const token = localStorage.getItem('token');
    const isRecent = (inv) => {
      const ref = inv.created_at || inv.invoice_date;
      return ref && (Date.now() - new Date(ref).getTime()) < 15 * 60 * 1000;
    };
    setInvoicePending(true);
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const res = await fetch(`${API_URL}/invoices`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          if (data.some(isRecent)) {
            setInvoices(sortInvoicesForDisplay(data));
            setInvoicePending(false);
            return true;
          }
        }
      } catch (err) {
        console.error('Erreur vérification facture :', err);
      }
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    toast.info('Votre facture sera disponible dans quelques instants');
    return false;
  };

  const checkPaymentStatus = async (sessionId) => {
    const token = localStorage.getItem('token');
    let attempts = 0;
    const maxAttempts = 15;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        toast.error('Délai de vérification du paiement dépassé. Veuillez actualiser la page.');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/subscriptions/checkout/status/${sessionId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.ok) {
          const data = await response.json();

          if (data.payment_status === 'paid') {
            setShowThankYou(true);
            toast.success('Paiement réussi ! Votre abonnement est actif.');

            window.history.replaceState({}, document.title, window.location.pathname);
            await fetchData();
            if (refreshUser) await refreshUser();
            setTimeout(() => setShowThankYou(false), 8000);
            waitForNewInvoice();
            return;
          } else if (data.subscription_processing) {
            // Async payment: checkout complete, payment processing (SEPA or bank transfer)
            setShowSepaProcessing(true);
            setProcessingPaymentMethod(data.payment_method_type || 'sepa_debit');
            const isBank = data.payment_method_type === 'customer_balance';
            toast.success(isBank
              ? 'Instructions de virement bancaire envoyées ! Le paiement est en cours de traitement.'
              : 'Mandat SEPA accepté ! Le prélèvement est en cours de traitement.');
            window.history.replaceState({}, document.title, window.location.pathname);
            await fetchData();
            if (refreshUser) await refreshUser();
            setTimeout(() => setShowSepaProcessing(false), 10000);
            return;
          } else if (data.status === 'expired') {
            toast.error('Session de paiement expirée');
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          }
          // Payment not yet confirmed, retry
          attempts++;
          setTimeout(poll, 2000);
        } else if (response.status === 404) {
          // Transaction not found yet - might be timing issue, retry
          console.log(`Vérification tentative ${attempts + 1}: transaction en cours de traitement...`);
          attempts++;
          setTimeout(poll, 2000);
        } else {
          // Other error - show message but keep trying
          const errorData = await response.json().catch(() => ({}));
          console.error('Erreur vérification paiement :', response.status, errorData);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          } else {
            toast.error(errorData.detail || 'Erreur lors de la vérification du paiement');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
      } catch (fetchError) {
        console.error('Erreur réseau :', fetchError);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.error('Erreur de connexion. Veuillez actualiser la page.');
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    };
    poll();
  };

  const handleSubscribe = async (pkg, planType, paymentMethod = 'card') => {
    if (!user?.structure_id) {
      toast.error('Utilisateur non lié à une structure');
      return;
    }

    setProcessingPayment(true);
    setSelectedPackage(pkg.code);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscriptions/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          structure_id: user.structure_id,
          package_code: pkg.code,
          plan_type: planType,
          payment_method: paymentMethod,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();
      
      if (response.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error(data.detail || 'Erreur lors de la création de l\'abonnement');
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(error.message || 'Erreur de connexion au serveur');
      setProcessingPayment(false);
      setSelectedPackage(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscriptions/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: cancelReason,
          cancel_at_period_end: true
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowCancelDialog(false);
        setCancelReason('');
        await fetchData();
      } else {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleRetryPaymentCB = async () => {
    setProcessingPayment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscriptions/retry-payment-cb`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur lors de la création du paiement');
      }
    } catch (error) {
      toast.error(error.message);
      setProcessingPayment(false);
    }
  };

  const downloadPdf = async (invoiceId, invoiceNumber) => {
    setDownloading(invoiceId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/invoices/${invoiceId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.content) {
          const byteCharacters = atob(data.content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = data.filename || `facture_${invoiceNumber}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          toast.success('Facture téléchargée');
        } else {
          toast.error('PDF non disponible');
        }
      } else {
        throw new Error('Failed to download');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(null);
    }
  };

  const downloadContract = async (contractId) => {
    if (!contractId) return;
    setDownloadingContract(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscription-contract/${contractId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.content) {
          const byteCharacters = atob(data.content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = data.filename || 'contrat_abonnement.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          toast.success('Contrat téléchargé');
        }
      } else {
        toast.error('Erreur lors du téléchargement du contrat');
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error('Erreur lors du téléchargement du contrat');
    } finally {
      setDownloadingContract(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getInvoiceStatusBadge = (status) => {
    const config = {
      paid: { label: 'Payée', cls: 'bg-green-100 text-green-800', icon: Check },
      pending: { label: 'En attente', cls: 'bg-yellow-100 text-yellow-800', icon: Clock },
      overdue: { label: 'En retard', cls: 'bg-red-100 text-red-800', icon: AlertTriangle },
    };
    const c = config[status] || { label: status, cls: 'bg-slate-100', icon: Clock };
    const Icon = c.icon;
    return (
      <Badge className={`${c.cls} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  // Access denied (skip when embedded in Admin page)
  if (!embedded && user && !ALLOWED_ROLES.includes(user.role)) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Lock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Accès restreint</h2>
            <p className="text-slate-500">
              Seuls les administrateurs, RH et comptabilité peuvent accéder à cette section.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F5156] mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Thank you message after payment
  if (showThankYou) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="subscription-thank-you">
        <Card className="border-2 border-green-500 bg-green-50 dark:bg-green-900/20">
          <CardContent className="pt-8 pb-8 text-center">
            <PartyPopper className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-green-800 dark:text-green-200 mb-2">
              Merci pour votre confiance !
            </h1>
            <p className="text-lg text-green-700 dark:text-green-300 mb-4">
              Votre abonnement est maintenant actif.
            </p>
            <div className="bg-white/50 rounded-lg p-4 mb-6 text-left max-w-sm mx-auto">
              <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Confirmation envoyée
              </h3>
              <p className="text-sm text-green-700">
                {invoicePending
                  ? 'Votre facture sera disponible dans quelques instants dans l\'onglet Factures. Un email de confirmation vous sera envoyé.'
                  : 'Un email de confirmation avec votre facture a été envoyé à l\'administrateur.'}
              </p>
            </div>
            <div className="flex flex-col gap-2 items-center">
              <Button onClick={() => setShowThankYou(false)} className="bg-green-600 hover:bg-green-700">
                Voir mon abonnement
              </Button>
              {contract && (
                <Button variant="outline" size="sm" onClick={() => downloadContract(contract.id)} disabled={downloadingContract}>
                  <Download className="w-4 h-4 mr-2" />
                  {downloadingContract ? 'Téléchargement...' : 'Télécharger votre contrat d\'abonnement'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showSepaProcessing) {
    const isBankTransfer = processingPaymentMethod === 'customer_balance';
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20">
          <CardContent className="pt-8 pb-8 text-center">
            <Clock className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-blue-800 dark:text-blue-200 mb-2">
              {isBankTransfer ? 'Virement bancaire en attente' : 'Prélèvement SEPA en cours'}
            </h1>
            <p className="text-lg text-blue-700 dark:text-blue-300 mb-4">
              {isBankTransfer
                ? 'Les instructions de virement bancaire vous ont été communiquées. Veuillez effectuer le virement depuis votre banque dans les prochains jours ouvrables (2 à 5 jours).'
                : 'Votre mandat de prélèvement SEPA a été accepté. Le premier prélèvement sera effectué dans les prochains jours ouvrables (5 à 14 jours).'}
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-6">
              Votre abonnement est actif dès maintenant. Vous avez accès à toutes les
              fonctionnalités de votre offre.
            </p>
            <div className="flex flex-col gap-2 items-center">
              <Button onClick={() => setShowSepaProcessing(false)} className="bg-blue-600 hover:bg-blue-700">
                Voir mon abonnement
              </Button>
              {contract && (
                <Button variant="outline" size="sm" onClick={() => downloadContract(contract.id)} disabled={downloadingContract}>
                  <Download className="w-4 h-4 mr-2" />
                  {downloadingContract ? 'Téléchargement...' : 'Télécharger votre contrat d\'abonnement'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Find current package
  const currentPackage = subscription ? packages.find(p => p.code === subscription.package_code) : null;

  return (
    <div className={embedded ? "" : "p-6 max-w-6xl mx-auto"} data-testid="subscription-page">
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Abonnement & Facturation</h1>
            <p className="text-slate-600 dark:text-slate-400">Gérez votre abonnement et consultez vos factures</p>
          </div>
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </Button>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="subscription" className="gap-2">
            <CreditCard className="w-4 h-4" />
            Abonnement
          </TabsTrigger>
          <TabsTrigger value="invoices" className="gap-2">
            <FileText className="w-4 h-4" />
            Factures ({invoices.length})
          </TabsTrigger>
        </TabsList>

        {/* ==================== SUBSCRIPTION TAB ==================== */}
        <TabsContent value="subscription">
          {/* Upgrade banner when redirected from locked module */}
          {isUpgradeMode && subscription && (
            <Card className="border-[#0F5156] bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 mb-6">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#0F5156]/10 rounded-full">
                    <Zap className="w-6 h-6 text-[#0F5156]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#0F5156] text-lg">Passez à un forfait supérieur</h3>
                    <p className="text-slate-600 dark:text-slate-400 mt-1">
                      Certains modules ne sont pas inclus dans votre abonnement actuel.
                      Choisissez un forfait avec plus de modules pour debloquer toutes les fonctionnalites.
                    </p>
                    {!allModulesAccess && allowedModules.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-slate-500 mb-2">Modules actuels :</p>
                        <div className="flex flex-wrap gap-1">
                          {allowedModules.map(mod => (
                            <Badge key={mod} variant="outline" className="text-xs gap-1">
                              <Check className="w-3 h-3 text-green-500" />
                              {MODULE_LABELS[mod] || mod}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setIsUpgradeMode(false)} className="text-slate-400">
                    ✕
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {subscription && subscription.status !== 'cancelled' ? (
            // Active subscription view
            <div className="space-y-6">
              {/* SEPA Payment Failed - Block and propose CB */}
              {subscription.status === 'payment_failed' && (
                <Card className="border-red-500 bg-red-50 dark:bg-red-900/20">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex flex-col items-center text-center gap-4">
                      <div className="p-3 bg-red-100 rounded-full">
                        <AlertTriangle className="w-8 h-8 text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-red-800 dark:text-red-200 mb-2">
                          {subscription.payment_method_type === 'customer_balance'
                            ? 'Échec du virement bancaire'
                            : 'Échec du prélèvement SEPA'}
                        </h3>
                        <p className="text-red-700 dark:text-red-300 mb-1">
                          {subscription.payment_method_type === 'customer_balance'
                            ? 'Votre virement bancaire n\'a pas été reçu. Votre accès à la plateforme est temporairement suspendu.'
                            : 'Votre prélèvement SEPA a échoué. Votre accès à la plateforme est temporairement suspendu.'}
                        </p>
                        <p className="text-sm text-red-600 dark:text-red-400">
                          Le paiement sera automatiquement retente le mois prochain.
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 w-full max-w-sm">
                        <Button
                          className="w-full bg-[#0F5156] hover:bg-[#0d464a]"
                          onClick={handleRetryPaymentCB}
                          disabled={processingPayment}
                        >
                          {processingPayment ? (
                            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Redirection...</>
                          ) : (
                            <><CreditCard className="w-4 h-4 mr-2" />Payer par carte bancaire maintenant</>
                          )}
                        </Button>
                        <p className="text-xs text-slate-500 text-center">
                          Réactiver immédiatement votre abonnement en payant par carte bancaire
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Warning if past due (card) */}
              {subscription.status === 'past_due' && (
                <Card className="border-red-500 bg-red-50">
                  <CardContent className="pt-4 flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                    <div>
                      <p className="text-red-800 font-medium">
                        Paiement en échec - Veuillez mettre à jour votre moyen de paiement
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Cancellation notice - More prominent */}
              {subscription.status === 'cancelling' && (
                <Card className="border-orange-400 bg-gradient-to-r from-orange-50 to-amber-50">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-orange-100 rounded-full">
                        <Clock className="w-6 h-6 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-orange-800 text-lg">Résiliation programmée</h3>
                        <p className="text-orange-700 mt-1">
                          Votre abonnement <strong>{currentPackage?.name || 'actuel'}</strong> reste actif jusqu'au <strong>{formatDate(subscription.current_period_end)}</strong>.
                        </p>
                        <p className="text-orange-600 text-sm mt-2">
                          Vous conservez l'accès à toutes vos fonctionnalités jusqu'à cette date.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Subscription Status Card */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      {subscription.status === 'cancelling' ? (
                        <>Abonne au {currentPackage ? currentPackage.name : 'Pack'}</>
                      ) : (
                        <>{currentPackage ? currentPackage.name : 'Abonnement'} - {subscription.plan_type === 'monthly' ? 'Mensuel' : 'Annuel'}</>
                      )}
                    </CardTitle>
                    <CardDescription>
                      {subscription.status === 'cancelling'
                        ? `Résiliation prévue le ${formatDate(subscription.current_period_end)}`
                        : 'Détails de votre abonnement actif'
                      }
                    </CardDescription>
                  </div>
                  {subscription.status === 'cancelling' ? (
                    <div className="flex flex-col items-end gap-1">
                      <Badge className="bg-green-100 text-green-800">
                        <Check className="w-4 h-4 mr-1" />
                        Encore actif
                      </Badge>
                      <Badge className="bg-orange-100 text-orange-800 text-xs">
                        Fin le {formatDate(subscription.current_period_end)}
                      </Badge>
                    </div>
                  ) : (
                    <Badge className={`${
                      subscription.status === 'active' ? 'bg-green-100 text-green-800'
                      : subscription.status === 'past_due' ? 'bg-red-100 text-red-800'
                      : 'bg-orange-100 text-orange-800'
                    }`}>
                      {subscription.status === 'active' && <Check className="w-4 h-4 mr-1" />}
                      {subscription.status === 'active' ? 'Actif' :
                       subscription.status === 'past_due' ? 'Paiement en attente' :
                       'Résiliation en cours'}
                    </Badge>
                  )}
                </CardHeader>
                <CardContent>
                  {/* Gratuity Banner */}
                  {subscription.is_free && (
                    <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-green-100 rounded-full">
                          <PartyPopper className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-green-800 text-lg">Abonnement offert</h3>
                          <p className="text-green-700 mt-1">
                            {subscription.free_reason || 'Votre abonnement est gratuit.'}
                          </p>
                          {subscription.free_until && (
                            <p className="text-green-600 text-sm mt-2">
                              Validité : jusqu'au {formatDate(subscription.free_until)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Tarif</p>
                        {subscription.is_free ? (
                          <div>
                            <p className="text-3xl font-bold text-green-600">
                              GRATUIT
                            </p>
                            <Badge className="mt-2 bg-green-100 text-green-800">
                              <Check className="w-4 h-4 mr-1" />
                              Offert
                            </Badge>
                          </div>
                        ) : (
                          <>
                            <p className="text-3xl font-bold text-[#0F5156]">
                              {formatCurrency(subscription.amount_ttc)}
                              <span className="text-lg font-normal text-slate-500">
                                TTC / {subscription.plan_type === 'monthly' ? 'mois' : 'an'}
                              </span>
                            </p>
                            <p className="text-sm text-slate-500">
                              ({formatCurrency(subscription.amount_ht)} HT + {formatCurrency(subscription.amount_tva)} TVA)
                            </p>
                          </>
                        )}
                      </div>

                      {currentPackage && (
                        <div>
                          <p className="text-sm text-slate-500 mb-2">Modules inclus</p>
                          <div className="flex flex-wrap gap-1">
                            {currentPackage.modules?.map(mod => {
                              const Icon = MODULE_ICONS[mod] || Settings;
                              return (
                                <Badge key={mod} variant="outline" className="gap-1">
                                  <Icon className="w-3 h-3" />
                                  {currentPackage.modules_labels?.[mod] || mod}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-4 md:border-l md:pl-6">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Période en cours</p>
                        <p className="font-medium">Du {formatDate(subscription.current_period_start)}</p>
                        <p className="font-medium">au {formatDate(subscription.current_period_end)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">
                          {subscription.status === 'cancelling' ? 'Fin d\'abonnement' : 'Prochain renouvellement'}
                        </p>
                        <p className="font-medium flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          {subscription.status === 'cancelling' ? (
                            <span className="text-orange-600">{formatDate(subscription.current_period_end)}</span>
                          ) : formatDate(subscription.current_period_end)}
                        </p>
                      </div>
                      {subscription.status === 'cancelling' ? (
                        <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                          <p className="text-sm text-orange-700 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            L'abonnement ne sera pas renouvelé après cette date
                          </p>
                        </div>
                      ) : (
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-700 flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Une facture sera générée automatiquement à chaque renouvellement
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment method and contract */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500">Moyen de paiement :</span>
                      {subscription.payment_method_type === 'sepa_debit' ? (
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Landmark className="w-3 h-3" />
                          Prélèvement SEPA
                        </Badge>
                      ) : subscription.payment_method_type === 'customer_balance' ? (
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          Virement bancaire
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          Carte bancaire
                        </Badge>
                      )}
                      {subscription.status === 'processing' && (
                        <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Paiement en cours de traitement
                        </Badge>
                      )}
                    </div>
                    {contract && (
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Contrat signé électroniquement le</p>
                          <p className="text-sm font-medium">{formatDate(contract.signed_at)}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => downloadContract(contract.id)} disabled={downloadingContract}>
                          <Download className="w-4 h-4 mr-2" />
                          {downloadingContract ? 'Téléchargement...' : 'Contrat'}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              {subscription.status !== 'cancelling' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Gérer mon abonnement</CardTitle>
                  </CardHeader>
                  <CardContent className="flex gap-4">
                    <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                      <Button 
                        variant="outline" 
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => setShowCancelDialog(true)}
                      >
                        Résilier l'abonnement
                      </Button>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Résilier votre abonnement</DialogTitle>
                          <DialogDescription>
                            Votre abonnement restera actif jusqu'à la fin de la période en cours ({formatDate(subscription.current_period_end)}).
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label>Raison de résiliation (optionnel)</Label>
                            <Textarea 
                              value={cancelReason}
                              onChange={(e) => setCancelReason(e.target.value)}
                              placeholder="Dites-nous pourquoi vous partez..."
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
                              Annuler
                            </Button>
                            <Button 
                              variant="destructive" 
                              onClick={handleCancelSubscription}
                              disabled={cancelling}
                            >
                              {cancelling ? 'Résiliation...' : 'Confirmer la résiliation'}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
              )}

              {/* Available upgrade packages */}
              {packages.filter(pkg => {
                if (!currentPackage) return false;
                return pkg.price_monthly_ht > (currentPackage.price_monthly_ht || 0) && pkg.code !== currentPackage.code;
              }).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-[#0F5156]" />
                      Forfaits superieurs disponibles
                    </CardTitle>
                    <CardDescription>
                      Passez à un forfait supérieur pour accéder à plus de modules
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {packages
                        .filter(pkg => currentPackage && pkg.price_monthly_ht > (currentPackage.price_monthly_ht || 0))
                        .map(pkg => {
                          const newModules = (pkg.modules || []).filter(m => !(currentPackage.modules || []).includes(m));
                          const price = billingCycle === 'monthly' ? pkg.price_monthly_ttc : pkg.price_annual_ttc;
                          const isProcessing = processingPayment && selectedPackage === pkg.code;
                          return (
                            <div key={pkg.code} className="border rounded-lg p-4 hover:border-[#0F5156] transition-colors">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-lg">{pkg.name}</h4>
                                <p className="text-lg font-bold text-[#0F5156]">
                                  {formatCurrency(price)}
                                  <span className="text-sm font-normal text-slate-500">
                                    /{billingCycle === 'monthly' ? 'mois' : 'an'}
                                  </span>
                                </p>
                              </div>
                              {newModules.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs text-slate-500 mb-1">Modules supplémentaires :</p>
                                  <div className="flex flex-wrap gap-1">
                                    {newModules.map(mod => (
                                      <Badge key={mod} className="bg-green-100 text-green-800 text-xs">
                                        + {MODULE_LABELS[mod] || mod}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <Button
                                className="w-full bg-[#0F5156] hover:bg-[#0d464a]"
                                onClick={() => { setPendingPackage(pkg); setPendingPlanType(billingCycle); setAcceptConditions(false); setShowPaymentMethodDialog(true); }}
                                disabled={processingPayment}
                                size="sm"
                              >
                                {isProcessing ? (
                                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Redirection...</>
                                ) : (
                                  <><Zap className="w-4 h-4 mr-2" />Passer à ce forfait</>
                                )}
                              </Button>
                            </div>
                          );
                        })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            // No subscription - Show package selection
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  Choisissez votre abonnement
                </h2>
                <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                  Sélectionnez le package qui correspond le mieux à vos besoins. Tous les paiements sont sécurisés via Stripe.
                </p>
              </div>

              {/* Billing Cycle Toggle */}
              <div className="flex justify-center">
                <Tabs value={billingCycle} onValueChange={setBillingCycle} className="w-auto">
                  <TabsList className="grid w-full grid-cols-2 max-w-xs">
                    <TabsTrigger value="monthly">Mensuel</TabsTrigger>
                    <TabsTrigger value="annual" className="relative">
                      Annuel
                      <Badge className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-1">
                        -10%
                      </Badge>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {packages.length === 0 ? (
                <Card className="max-w-md mx-auto">
                  <CardContent className="pt-8 pb-8 text-center">
                    <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold mb-2">Aucun package disponible</h2>
                    <p className="text-slate-500">
                      Contactez l'administrateur pour configurer les offres d'abonnement.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {packages.map((pkg, index) => {
                    const isPopular = index === 1 || pkg.code === 'pro';
                    const price = billingCycle === 'monthly' ? pkg.price_monthly_ttc : pkg.price_annual_ttc;
                    const priceHT = billingCycle === 'monthly' ? pkg.price_monthly_ht : pkg.price_annual_ht;
                    const periodLabel = billingCycle === 'monthly' ? '/mois' : '/an';
                    const isProcessing = processingPayment && selectedPackage === pkg.code;

                    // Check if user has an active/cancelling subscription
                    const hasActiveSubscription = subscription && ['active', 'cancelling'].includes(subscription.status);
                    const isCurrentPackage = currentPackage?.code === pkg.code;
                    const currentPrice = currentPackage?.price_monthly_ht || 0;
                    const pkgPrice = pkg.price_monthly_ht || 0;
                    const isUpgrade = hasActiveSubscription && pkgPrice > currentPrice;
                    const isDowngrade = hasActiveSubscription && pkgPrice < currentPrice && !isCurrentPackage;
                    const canSubscribe = !hasActiveSubscription || isUpgrade;

                    // Calculate prorata credit for upgrade
                    let prorataCredit = 0;
                    if (isUpgrade && subscription) {
                      try {
                        const periodEnd = new Date(subscription.current_period_end);
                        const periodStart = new Date(subscription.current_period_start);
                        const now = new Date();
                        const totalDays = Math.ceil((periodEnd - periodStart) / (1000 * 60 * 60 * 24));
                        const remainingDays = Math.ceil((periodEnd - now) / (1000 * 60 * 60 * 24));
                        if (remainingDays > 0 && totalDays > 0) {
                          const dailyRate = subscription.amount_ttc / totalDays;
                          prorataCredit = (dailyRate * remainingDays).toFixed(2);
                        }
                      } catch (e) { /* ignore */ }
                    }

                    return (
                      <Card
                        key={pkg.code}
                        className={`relative ${isPopular ? 'border-2 border-[#0F5156] shadow-lg' : ''} ${isCurrentPackage ? 'ring-2 ring-green-500' : ''} ${isDowngrade ? 'opacity-60' : ''}`}
                      >
                        {isCurrentPackage && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                            <Badge className="bg-green-600 text-white flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              Abonnement actuel
                            </Badge>
                          </div>
                        )}
                        {!isCurrentPackage && isPopular && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                            <Badge className="bg-[#0F5156] text-white flex items-center gap-1">
                              <Star className="w-3 h-3" />
                              Populaire
                            </Badge>
                          </div>
                        )}
                        
                        <CardHeader className="text-center pb-2">
                          <div className="w-12 h-12 bg-[#0F5156]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Package className="w-6 h-6 text-[#0F5156]" />
                          </div>
                          <CardTitle className="text-xl">{pkg.name}</CardTitle>
                          {pkg.description && (
                            <CardDescription>{pkg.description}</CardDescription>
                          )}
                        </CardHeader>
                        
                        <CardContent className="text-center">
                          <div className="mb-6">
                            <p className="text-4xl font-bold text-[#0F5156]">
                              {formatCurrency(price)}
                              <span className="text-lg font-normal text-slate-500">{periodLabel}</span>
                            </p>
                            <p className="text-sm text-slate-500">
                              {formatCurrency(priceHT)} HT
                            </p>
                            {billingCycle === 'annual' && pkg.annual_savings > 0 && (
                              <Badge className="mt-2 bg-green-100 text-green-800">
                                Économisez {formatCurrency(pkg.annual_savings)}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="space-y-3 mb-6 text-left">
                            {pkg.modules?.map(mod => {
                              const label = pkg.modules_labels?.[mod] || mod;
                              return (
                                <div key={mod} className="flex items-center gap-2">
                                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                    <Check className="w-3 h-3 text-green-600" />
                                  </div>
                                  <span className="text-sm">{label}</span>
                                </div>
                              );
                            })}
                            {pkg.max_employees ? (
                              <div className="flex items-center gap-2">
                                <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                  <Users className="w-3 h-3 text-blue-600" />
                                </div>
                                <span className="text-sm">Jusqu'à {pkg.max_employees} employés</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                  <Zap className="w-3 h-3 text-purple-600" />
                                </div>
                                <span className="text-sm">Employés illimités</span>
                              </div>
                            )}
                          </div>
                          
                          {isCurrentPackage ? (
                            <Button className="w-full" variant="outline" disabled>
                              <Check className="w-4 h-4 mr-2" />
                              Votre abonnement actuel
                            </Button>
                          ) : isDowngrade ? (
                            <div className="space-y-2">
                              <Button className="w-full" variant="outline" disabled>
                                <Lock className="w-4 h-4 mr-2" />
                                Non disponible
                              </Button>
                              <p className="text-xs text-slate-500 text-center">
                                Attendez la fin de votre période actuelle
                              </p>
                            </div>
                          ) : isUpgrade ? (
                            <div className="space-y-2">
                              <Button
                                className="w-full bg-green-600 hover:bg-green-700"
                                onClick={() => { setPendingPackage(pkg); setPendingPlanType(billingCycle); setAcceptConditions(false); setShowPaymentMethodDialog(true); }}
                                disabled={processingPayment}
                              >
                                {isProcessing ? (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    Redirection...
                                  </>
                                ) : (
                                  <>
                                    <Zap className="w-4 h-4 mr-2" />
                                    Passer à ce pack
                                  </>
                                )}
                              </Button>
                              {prorataCredit > 0 && (
                                <p className="text-xs text-green-600 text-center">
                                  Credit prorata: -{formatCurrency(prorataCredit)}
                                </p>
                              )}
                            </div>
                          ) : (
                            <Button
                              className={`w-full ${isPopular ? 'bg-[#0F5156] hover:bg-[#0d464a]' : ''}`}
                              variant={isPopular ? 'default' : 'outline'}
                              onClick={() => { setPendingPackage(pkg); setPendingPlanType(billingCycle); setAcceptConditions(false); setShowPaymentMethodDialog(true); }}
                              disabled={processingPayment}
                            >
                              {isProcessing ? (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                  Redirection vers Stripe...
                                </>
                              ) : (
                                <>
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  S'abonner
                                </>
                              )}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Trust badges */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-8 text-slate-500">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    <span className="text-sm">Paiement sécurisé</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    <span className="text-sm">Powered by Stripe</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    <span className="text-sm">Annulation à tout moment</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ==================== INVOICES TAB ==================== */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Historique des factures
                  </CardTitle>
                  <CardDescription>
                    Consultez et téléchargez vos factures d'abonnement
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${API_URL}/invoices/sync-stripe`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await res.json();
                      if (res.ok) {
                        toast.success(data.message);
                        await fetchData();
                      } else {
                        toast.error(data.detail || 'Erreur de synchronisation');
                      }
                    } catch { toast.error('Erreur de connexion'); }
                  }}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Synchroniser les factures
                  </Button>
                  <Button variant="outline" size="sm" onClick={async () => {
                    if (!window.confirm('Recalculer les montants des factures existantes à partir des paiements Stripe réels ? Les PDF seront régénérés.')) return;
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${API_URL}/invoices/recalculate-amounts`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await res.json();
                      if (res.ok) {
                        toast.success(data.message);
                        await fetchData();
                      } else {
                        toast.error(data.detail || 'Erreur de recalcul');
                      }
                    } catch { toast.error('Erreur de connexion'); }
                  }}>
                    <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
                    Corriger les montants
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-2">Aucune facture</p>
                  <p className="text-sm text-slate-400">
                    Les factures apparaîtront ici après votre premier paiement
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N° Facture</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead>Montant TTC</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-mono font-medium">
                          {invoice.invoice_number}
                        </TableCell>
                        <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">
                            {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                          </span>
                        </TableCell>
                        <TableCell className="font-semibold">
                          {invoice.is_free ? (
                            <Badge className="bg-green-100 text-green-800">GRATUIT</Badge>
                          ) : (
                            formatCurrency(invoice.amount_ttc)
                          )}
                        </TableCell>
                        <TableCell>
                          {getInvoiceStatusBadge(invoice.status)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadPdf(invoice.id, invoice.invoice_number)}
                            disabled={downloading === invoice.id}
                          >
                            {downloading === invoice.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-2" />
                                PDF
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Method Selection Dialog */}
      <Dialog open={showPaymentMethodDialog} onOpenChange={(open) => { setShowPaymentMethodDialog(open); if (!open) { setPendingPackage(null); setPendingPlanType(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Choisissez votre moyen de paiement</DialogTitle>
            <DialogDescription>
              Selectionnez comment vous souhaitez payer votre abonnement
              {pendingPackage && <span className="font-medium"> {pendingPackage.name}</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 py-4">
            <div
              className="cursor-pointer border-2 rounded-lg p-6 text-center hover:border-[#0F5156] hover:bg-[#0F5156]/5 transition-all"
              onClick={() => {
                if (!acceptConditions) { toast.error('Veuillez accepter les conditions d\'abonnement'); return; }
                setShowPaymentMethodDialog(false);
                handleSubscribe(pendingPackage, pendingPlanType, 'card');
              }}
            >
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-[#0F5156]" />
              <h3 className="font-semibold text-base">Carte bancaire</h3>
              <p className="text-xs text-slate-500 mt-1">Visa, Mastercard, etc.</p>
              <p className="text-xs text-slate-400 mt-2">Paiement immediat</p>
            </div>
            <div
              className="cursor-pointer border-2 rounded-lg p-6 text-center hover:border-[#0F5156] hover:bg-[#0F5156]/5 transition-all"
              onClick={() => {
                if (!acceptConditions) { toast.error('Veuillez accepter les conditions d\'abonnement'); return; }
                setShowPaymentMethodDialog(false);
                handleSubscribe(pendingPackage, pendingPlanType, 'sepa_debit');
              }}
            >
              <Landmark className="w-12 h-12 mx-auto mb-3 text-[#0F5156]" />
              <h3 className="font-semibold text-base">Prélèvement SEPA</h3>
              <p className="text-xs text-slate-500 mt-1">Débit automatique mensuel</p>
              <p className="text-xs text-slate-400 mt-2">Prélèvement sous 5-14 jours</p>
            </div>
            <div
              className="cursor-pointer border-2 rounded-lg p-6 text-center hover:border-[#0F5156] hover:bg-[#0F5156]/5 transition-all"
              onClick={() => {
                if (!acceptConditions) { toast.error('Veuillez accepter les conditions d\'abonnement'); return; }
                setShowPaymentMethodDialog(false);
                handleSubscribe(pendingPackage, pendingPlanType, 'customer_balance');
              }}
            >
              <Building2 className="w-12 h-12 mx-auto mb-3 text-[#0F5156]" />
              <h3 className="font-semibold text-base">Virement bancaire</h3>
              <p className="text-xs text-slate-500 mt-1">Virement depuis votre banque</p>
              <p className="text-xs text-slate-400 mt-2">Traitement sous 2-5 jours</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border">
            <Checkbox
              id="accept-conditions"
              checked={acceptConditions}
              onCheckedChange={setAcceptConditions}
              className="mt-0.5"
            />
            <Label htmlFor="accept-conditions" className="text-sm leading-tight cursor-pointer">
              J'accepte les conditions générales d'abonnement à la plateforme Nexus SIRH
              et autorise la génération d'un contrat d'abonnement signé électroniquement en mon nom.
            </Label>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subscription;
